/* eslint-disable max-len */
import { EntryMethodPreference, EntryMethodPreferenceOption } from '@acp/shared';
import { format, startOfDay } from 'date-fns';
import { z } from 'zod';

const MAX_COMMENTS_LENGTH = 500;

enum ReferenceValue {
    ALLOW_UNTIL_DATE = '0',
    ALWAYS_ALLOWED = '1'
}

const requiredPreferences: Record<string, z.ZodTypeAny> = {};
const requiredTempPreferences: Record<string, z.ZodTypeAny> = {};

for (const preferenceKey of Object.keys(EntryMethodPreference)) {
    requiredPreferences[preferenceKey] = z.nativeEnum(EntryMethodPreferenceOption).nullable().optional();

    requiredTempPreferences[preferenceKey] = z.nativeEnum(EntryMethodPreferenceOption).nullable().optional();
    requiredTempPreferences[`${preferenceKey}ExpiresAt`] = z.coerce.date().nullable().optional();
}

const entryMethodFormSchema = z.object({
    // fields to hold value for the radio button
    fieldOption: z.object({
        occupantAppointmentPreference: z.nativeEnum(ReferenceValue).optional(),
        internalEnPreference: z.nativeEnum(ReferenceValue).optional(),
        vacantEntryPreference: z.nativeEnum(ReferenceValue).optional(),
    }),

    // form fields
    preferences: z.object(requiredPreferences),
    tempPreferences: z.object(requiredTempPreferences).optional(),
    comments: z.string().optional(),
    tempExpireDateSettings: z.object({
        min: z.string(),
        max: z.string(),
    }).optional(),
}).superRefine((val, ctx) => {
    if (val.comments != null) {
        let trimmed = val.comments
            .replace(/<[^>]*(>|$)|&nbsp;/g, '') // trim nbsp
            .replace(/(<([^>]+)>)/gi, '') // trim html tags
            .replace(/(?:\r\n|\r|\n)/g, ''); // trim line break

        if (trimmed.length >= MAX_COMMENTS_LENGTH) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['comments'],
                message: `Comment should be ${MAX_COMMENTS_LENGTH} characters or less`,
            });
        }
    }

    // validate date
    if (val.tempExpireDateSettings) {
        const fromDate = new Date(val.tempExpireDateSettings.min);
        const toDate = new Date(val.tempExpireDateSettings.max);

        // run thourgh all the ExpiresAt keys
        for (const key of Object.keys(val.tempPreferences || {})) {
            if (key.endsWith('ExpiresAt')) {
                const originalKey = key.replace('ExpiresAt', '');

                // validate only if its key has value (ex: internalEnPreference = 1)
                if (val.tempPreferences?.[originalKey] === EntryMethodPreferenceOption.YES) {
                    // if there is no data for the exp date
                    if (val.tempPreferences[key] === null || val.tempPreferences[key] === undefined) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: 'Expiration date is required',
                            path: [`tempPreferences.${key}`],
                        });
                    } else {
                        // validate date range
                        const selectedDate = startOfDay(new Date(val.tempPreferences[key] as Date));
                        if (selectedDate < startOfDay(fromDate) || selectedDate > startOfDay(toDate)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Expiration date must be between ${format(fromDate, 'dd/MM/yyyy')} and ${format(toDate, 'dd/MM/yyyy')}`,
                                path: [`tempPreferences.${key}`],
                            });
                        }
                    }
                }
            }
        }
    }
});

type EntryMethodFormValues = z.infer<typeof entryMethodFormSchema>;

export { entryMethodFormSchema, EntryMethodFormValues, ReferenceValue };
